const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

const toDate = (value: string | null | undefined): Date | null => {
    if (!value) return null

    const date = DATE_ONLY.test(value) ? new Date(`${value}T00:00:00.000Z`) : new Date(value)

    return Number.isNaN(date.getTime()) ? null : date
}

/** Dates are always formatted in UTC so the server and the browser render the same text. */
export const formatDate = (value: string | null | undefined, style: 'long' | 'short' | 'month' = 'long'): string => {
    const date = toDate(value)

    if (!date) return ''

    const options: Intl.DateTimeFormatOptions = style === 'long'
        ? { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }
        : style === 'short'
            ? { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }
            : { month: 'long', year: 'numeric', timeZone: 'UTC' }

    return new Intl.DateTimeFormat('en-GB', options).format(date)
}

export const formatYear = (value: string | null | undefined): string => {
    const date = toDate(value)

    return date ? String(date.getUTCFullYear()) : ''
}

export const toIsoDate = (value: string | null | undefined): string | null => {
    const date = toDate(value)

    return date ? date.toISOString() : null
}

export const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '0:00'

    const total = Math.floor(seconds)
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const rest = total % 60

    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`

    return `${minutes}:${String(rest).padStart(2, '0')}`
}

export const stripHtml = (html: string | null | undefined): string => {
    if (!html) return ''

    return html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|h[1-6]|li|blockquote|div)>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
}

export const readingMinutes = (html: string | null | undefined): number => {
    const words = stripHtml(html).split(' ').filter(Boolean).length

    return Math.max(1, Math.round(words / 200))
}

export const truncate = (text: string | null | undefined, length: number): string => {
    if (!text) return ''
    if (text.length <= length) return text

    return `${text.slice(0, length).replace(/\s+\S*$/, '')}...`
}

export const plural = (count: number, singular: string, pluralForm?: string): string =>
    `${count} ${count === 1 ? singular : (pluralForm ?? `${singular}s`)}`

export const isExternal = (href: string): boolean => /^https?:\/\//i.test(href)
